import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Global success-overlay context. Renders a 250x250px image in the middle of
 * the screen for a configurable duration. Used after sale completion, return
 * completion, and customer auto-clear.
 */
const SuccessOverlayContext = createContext(null);

const DEFAULT_DURATION_MS = 2000;
const DEFAULT_IMAGE = "/success-check.png";

export function SuccessOverlayProvider({ children }) {
    const [overlay, setOverlay] = useState(null); // { src, duration, borderColor, key }

    const show = useCallback((opts = {}) => {
        setOverlay({
            src: opts.src || DEFAULT_IMAGE,
            duration: opts.duration || DEFAULT_DURATION_MS,
            borderColor: opts.borderColor || "#1F8A3E",
            key: Date.now(),
        });
    }, []);

    const showCustomerCleared = useCallback(() => {
        setOverlay({
            src: "/customer-cleared.png",
            duration: 1000,
            borderColor: "#FF3B30",
            label: "ΤΕΛΟΣ ΧΡΟΝΟΥ",
            key: Date.now(),
        });
    }, []);

    useEffect(() => {
        if (!overlay) return;
        const t = setTimeout(() => setOverlay(null), overlay.duration);
        return () => clearTimeout(t);
    }, [overlay]);

    return (
        <SuccessOverlayContext.Provider value={{ show, showCustomerCleared }}>
            {children}
            {overlay && (
                <div
                    role="status"
                    aria-label="Ειδοποίηση"
                    data-testid="success-overlay"
                    className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none animate-fade-in"
                    key={overlay.key}
                >
                    <div
                        className="bg-white shadow-2xl border-2 flex flex-col items-center justify-center"
                        style={{ width: 250, height: 250, borderColor: overlay.borderColor }}
                    >
                        <div
                            style={{
                                width: 200,
                                height: overlay.label ? 150 : 220,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={overlay.src}
                                alt=""
                                style={{
                                    width: "100%",
                                    // Slightly larger than container so the bottom watermark
                                    // is cropped out (we shift the image up by 5%).
                                    height: overlay.label ? "112%" : "100%",
                                    objectFit: "contain",
                                    objectPosition: "center top",
                                }}
                                draggable={false}
                            />
                        </div>
                        {overlay.label && (
                            <div
                                className="mt-1 font-heading font-black tracking-tighter text-2xl leading-none"
                                style={{ color: overlay.borderColor }}
                            >
                                {overlay.label}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </SuccessOverlayContext.Provider>
    );
}

export function useSuccessOverlay() {
    const ctx = useContext(SuccessOverlayContext);
    if (!ctx) {
        return { show: () => {}, showCustomerCleared: () => {} };
    }
    return ctx;
}
